/**
 * @fileoverview Reactivate User manager prefill hook.
 * @description Prefills the Reactivate form's manager field, once per role, when that role's
 * manager choices have settled: the manager the user had if it is still a choice, otherwise the
 * only choice, otherwise the field is left for the admin. It only ever fills an empty field and
 * never clears anything, so a manager the admin picked is never replaced or blanked by a
 * background refetch of the choices (a lifecycle invalidation, a window refocus). It waits for the
 * choices to settle, so the cached list of a role the admin switched back to is not used for the
 * prefill while it is being refetched.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useEffect, useRef } from 'react'

import type { ReactivateUserFormValues } from '@/features/users/schemas/reactivate-user-form.schema'
import type { ReportingManagerOption } from '@/shared/types/directory-options.types'
import type { UseFormReturn } from 'react-hook-form'

interface ManagerPrefillOptions {
  form: UseFormReturn<ReactivateUserFormValues>
  /** The chosen role. */
  roleId: string
  /** The role's manager choices, once loaded. */
  managers: ReportingManagerOption[] | undefined
  /** Whether the choices are loaded and not being refetched. */
  settled: boolean
  /** The manager the user had. */
  previousManagerId: string | undefined
}

/**
 * @description Prefills the manager field once per role, when the role's choices have settled and
 * the admin has not chosen a manager.
 * @param {ManagerPrefillOptions} options - The form, the role, its manager choices, whether they
 * have settled, and the manager the user had.
 */
export function useManagerPrefill({
  form,
  roleId,
  managers,
  settled,
  previousManagerId,
}: ManagerPrefillOptions) {
  // The role whose settled choices have been offered a prefill already.
  const prefilledRoleId = useRef<string | null>(null)

  /**
   * @description Prefills an empty manager field the first time a role's choices settle.
   */
  useEffect(() => {
    if (!managers || !settled || prefilledRoleId.current === roleId) {
      return
    }
    prefilledRoleId.current = roleId
    if (form.getValues('reportingManagerUserId') !== '') {
      return
    }
    const previous = managers.find((manager) => manager.userId === previousManagerId)
    const onlyChoice = managers.length === 1 ? managers[0] : undefined
    const prefill = previous ?? onlyChoice
    if (prefill) {
      form.setValue('reportingManagerUserId', prefill.userId)
    }
  }, [managers, settled, roleId, form, previousManagerId])
}
